import cx from 'classnames';
import { forwardRef, useMemo } from 'react';
import type { ReactNode } from 'react';

import type { PanelNames } from '@trello/split-screen';
import { useSplitScreenSharedState } from '@trello/split-screen';
import { getTestId } from '@trello/test-ids';
import type { SplitScreenTestIds } from '@trello/test-ids/';

import * as styles from './CollapsibleSplitScreenPanel.module.less';

interface CollapsibleSplitScreenPanelProps {
  children?: ReactNode;
  panelName: PanelNames;
  isHidden?: boolean;
  panelWidth?: number;
  enableTransitions?: boolean;
  isCollapsed?: boolean;
  shouldGrow?: boolean;
}

export const CollapsibleSplitScreenPanel = forwardRef<
  HTMLDivElement,
  CollapsibleSplitScreenPanelProps
>(
  (
    {
      children,
      isHidden,
      panelName,
      panelWidth,
      enableTransitions,
      isCollapsed,
      shouldGrow = false,
    },
    ref,
  ) => {
    const { areMultiplePanelsOpen } = useSplitScreenSharedState();

    const panelFlexValue = useMemo(() => {
      if (panelName === 'boardSwitcher' && !isHidden && areMultiplePanelsOpen) {
        return '0 0 272px';
      } else if (
        panelName === 'boardSwitcher' &&
        !isHidden &&
        !areMultiplePanelsOpen
      ) {
        return '1 1 100%';
      }

      if (isHidden || panelWidth === 0) {
        return '0 0 0px';
      }

      if (!areMultiplePanelsOpen || shouldGrow) {
        return `1 1 ${panelWidth || 0}px`;
      } else if (panelName !== 'board' && areMultiplePanelsOpen) {
        return `0 0 ${panelWidth || 0}px`;
      }

      return `1 1 ${panelWidth || 0}px`;
    }, [isHidden, panelWidth, areMultiplePanelsOpen, shouldGrow, panelName]);

    return (
      <>
        <div
          className={cx(styles.panel, styles[panelName], {
            [styles.singlePanel]: !areMultiplePanelsOpen,
            [styles.panelTransitions]: enableTransitions,
            [styles.hidden]: isHidden,
            [styles.collapsed]: isCollapsed,
            [styles.boardSwitcherPanel]: panelName === 'boardSwitcher',
          })}
          data-testid={getTestId<SplitScreenTestIds>('split-screen-panel')}
          style={{
            flex: panelFlexValue,
          }}
          ref={ref}
          aria-hidden={isHidden}
        >
          {children}
        </div>
      </>
    );
  },
);
